import { Component, ReactNode } from "react";
import { AlertTriangle } from "lucide-react";
import { Button } from "./button";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log detailed error information for debugging
    console.error("=== ErrorBoundary caught an error ===");
    console.error("Error:", error);
    console.error("Error message:", error?.message);
    console.error("Error stack:", error?.stack);
    console.error("Component stack:", errorInfo?.componentStack);

    this.setState({ errorInfo });
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex flex-col items-center justify-center p-8 text-center">
          <AlertTriangle className="h-12 w-12 text-yellow-500 mb-4" />
          <h2 className="text-lg font-semibold mb-2">Something went wrong</h2>
          <p className="text-muted-foreground mb-4 max-w-md">
            There was an error displaying this content. This may be due to invalid data.
          </p>
          <p className="text-sm text-red-500 mb-4 font-mono max-w-md overflow-auto">
            {this.state.error?.message}
          </p>
          {this.state.errorInfo?.componentStack && (
            <details className="mb-4 text-left max-w-lg">
              <summary className="cursor-pointer text-sm text-muted-foreground">
                Show component stack
              </summary>
              <pre className="mt-2 p-2 bg-muted rounded text-xs overflow-auto max-h-48">
                {this.state.errorInfo.componentStack}
              </pre>
            </details>
          )}
          <Button
            variant="outline"
            onClick={() => this.setState({ hasError: false, error: null, errorInfo: null })}
          >
            Try Again
          </Button>
        </div>
      );
    }

    return this.props.children;
  }
}
